import { Takeover } from '@schibsted/advertory/aftenposten/cjs';
import React, { useEffect, useRef } from 'react';

import type { OptionalDataFailed } from '../../../../core/fetching/index.js';
import type { SiteVersion } from '../../../../core/system/rendering/types.js';
import type {
  ByfunnFrapiArticle,
  GodtTeasers,
  Recommendations,
} from '../../../types/frapi-types.js';
import type { ArticleProps } from '../../components/articles/types.js';

import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';
import {
  useByfunnPulseContext,
  buildArticlePageView,
  withByfunnPulse,
} from '../../../pulse/index.js';
import { AdContexts } from '../../../types/ad-types.js';
import { insertAdsPlacements } from '../../components/ads/Ad/helpers.js';
import {
  isReview,
  shouldDisplayAds,
} from '../../components/articles/helpers.js';
import { DataController } from '../../components/commonComponents/DataController.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import { useAdjustAdvertoryTakeoverStyles } from '../../hooks/useAdjustAdvertoryTakeoverStyles.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import { ArticleType } from './ArticleType.js';

type ArticlePageProps = {
  article: ByfunnFrapiArticle;
  recommendations: Recommendations | OptionalDataFailed;
  godtTeasers: GodtTeasers | OptionalDataFailed;
  siteVersion: SiteVersion;
  isMobile?: boolean;
  isHermesApp?: boolean;
  enableRuterCampaign: boolean;
};

const ArticlePage: React.FC<ArticlePageProps> = ({
  article,
  recommendations,
  godtTeasers,
  isMobile = false,
  siteVersion,
  isHermesApp,
  enableRuterCampaign,
}) => {
  const {
    registerPageLeaveTracking,
    getEnvironmentId,
    trackViewArticle,
    registerViewElementTracking,
  } = useByfunnPulseContext();

  const session = useSessionData();
  const articleElementRef = useRef(null);

  useEffect(() => {
    if (!articleElementRef.current) {
      return;
    }

    const { legacy, tpaas } = buildArticlePageView(article);

    trackViewArticle(legacy, tpaas, articleElementRef.current);
    registerViewElementTracking();
  }, [trackViewArticle, article, registerViewElementTracking]);

  useEffect(() => {
    if (articleElementRef.current) {
      registerPageLeaveTracking({
        objectElement: articleElementRef.current,
      });
    }
  }, [registerPageLeaveTracking]);

  useAdjustAdvertoryTakeoverStyles();

  useInitializeAds(getEnvironmentId, session, siteVersion);

  const rendersReviewArticle =
    article.articleTemplate === 'review' ||
    (article.articleTemplate === 'automatic' && isReview(article));
  const articleContext = rendersReviewArticle
    ? AdContexts.REVIEW
    : AdContexts.GUIDE;
  const componentsWithAds = shouldDisplayAds(article)
    ? insertAdsPlacements(isMobile, article.components, articleContext)
    : article.components;

  const commonProps: ArticleProps = {
    article: { ...article, components: componentsWithAds },
    isMobile,
    recommendations,
    godtTeasers,
    isHermesApp,
    forwardedRef: articleElementRef,
    enableRuterCampaign,
  };

  return (
    <ListsProvider>
      <Takeover targetId="takeover" />
      <div
        className={`${siteVersion}-article-container advertory-container-anchor`}
      >
        <ArticleType article={article} commonProps={commonProps} />
        <DataController />
      </div>
    </ListsProvider>
  );
};

const ArticlePageWithPulse = withByfunnPulse(ArticlePage);

export { ArticlePageWithPulse as ArticlePage };
